import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
    Tooltip,
    TooltipContent,
    type TooltipContentProps,
    TooltipProvider,
    TooltipTrigger,
} from 'src/registry/ui/Tooltip';
import { cn } from 'src/utils/shadcn';

interface FormFieldBlockProps extends React.HTMLAttributes<HTMLDivElement> {}
function FormFieldBlock({ className, children, ...props }: FormFieldBlockProps) {
    return (
        <div className={cn('flex flex-col gap-2 md:flex-row md:gap-4', className)} {...props}>
            {children}
        </div>
    );
}

interface FormFieldBlockLabelProps extends React.HTMLAttributes<HTMLDivElement> {}
function FormFieldBlockLabel({ className, children, ...props }: FormFieldBlockLabelProps) {
    return (
        <div className={cn('flex shrink-0 flex-col md:w-1/2 lg:w-96', className)} {...props}>
            {children}
        </div>
    );
}

interface FormFieldBlockTitleProps extends React.HTMLAttributes<HTMLDivElement> {}
function FormFieldBlockTitle({ className, children, ...props }: FormFieldBlockTitleProps) {
    return (
        <div className={cn('flex items-center gap-1', className)} {...props}>
            {children}
        </div>
    );
}

interface FormFieldBlockNameProps extends React.HTMLAttributes<HTMLHeadingElement> {}
function FormFieldBlockName({ className, children, ...props }: FormFieldBlockNameProps) {
    return (
        <h4 className={cn('text-sm font-semibold', className)} {...props}>
            {children}
        </h4>
    );
}

interface FormFieldBlockTooltipProps extends TooltipContentProps {}
function FormFieldBlockTooltip({ children, ...props }: FormFieldBlockTooltipProps) {
    return (
        <TooltipProvider delayDuration={300}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <FontAwesomeIcon icon={faCircleQuestion} className="text-sm text-gray-400 hover:text-gray-500" />
                </TooltipTrigger>
                <TooltipContent {...props}>{children}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

interface FormFieldBlockSubtitleProps extends React.HTMLAttributes<HTMLHeadingElement> {}
function FormFieldBlockSubtitle({ className, children, ...props }: FormFieldBlockSubtitleProps) {
    return (
        <p className={cn('text-sm text-gray-500', className)} {...props}>
            {children}
        </p>
    );
}

interface FormFieldBlockBodyProps extends React.HTMLAttributes<HTMLDivElement> {}
function FormFieldBlockBody({ className, children, ...props }: FormFieldBlockBodyProps) {
    return (
        <div className={cn('flex flex-col gap-2.5 md:w-1/2 lg:flex-row lg:flex-wrap lg:gap-4', className)} {...props}>
            {children}
        </div>
    );
}

interface FormFieldBlockSeparatorProps extends React.HTMLAttributes<HTMLHRElement> {}
function FormFieldBlockSeparator({ className, ...props }: FormFieldBlockSeparatorProps) {
    return <hr className={cn('h-px bg-gray-200', className)} {...props} />;
}

export {
    FormFieldBlock,
    FormFieldBlockLabel,
    FormFieldBlockTitle,
    FormFieldBlockName,
    FormFieldBlockTooltip,
    FormFieldBlockSubtitle,
    FormFieldBlockBody,
    FormFieldBlockSeparator,
};
